import {
  FormControl,
  InputLabel,
  Typography,
  Box,
  Chip,
  SxProps,
  Theme,
} from '@mui/material';
import { BarChart, Bolt, RateReview } from '@mui/icons-material';
import { FC } from 'react';
import {
  ArticleFormat,
  BrandKey,
  UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier,
  GetQualifiersQuery_query_qualifiers_Qualifier,
  QualifierOrderingInput,
  QualifierStatus,
  QualifierType,
} from '../../../__generated__/queries-web';
import { useQualifiers } from './getQualifers.web.graphql';
import { useStyles } from './styles';

type Qualifier = GetQualifiersQuery_query_qualifiers_Qualifier;
type ArticleQualifier = UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier;

interface QualifierTypeConfig {
  type: QualifierType;
  Icon: typeof BarChart;
  main: string;
  hover: string;
  iconSx?: SxProps<Theme>;
}

interface QualifierGroupConfig {
  label: string;
  types: readonly QualifierType[];
}

const QUALIFIER_TYPE_CONFIG: readonly QualifierTypeConfig[] = [
  {
    type: QualifierType.Trigger,
    Icon: Bolt,
    main: 'rgb(102, 108, 255)',
    hover: 'rgb(82, 86, 204)',
  },
  {
    type: QualifierType.IntroAvis,
    Icon: RateReview,
    main: 'rgb(88, 140, 180)',
    hover: 'rgb(70, 112, 144)',
    iconSx: { paddingLeft: '1.5px' },
  },
  {
    type: QualifierType.Statistical,
    Icon: BarChart,
    main: 'rgb(13, 148, 136)',
    hover: 'rgb(10, 118, 109)',
  },
];

const QUALIFIER_GROUPS: readonly QualifierGroupConfig[] = [
  {
    label: 'Déclencheurs',
    types: [QualifierType.Trigger, QualifierType.IntroAvis],
  },
  {
    label: 'Statistiques',
    types: [QualifierType.Statistical],
  },
];

const qualifierTypeConfigByType = QUALIFIER_TYPE_CONFIG.reduce<
  Partial<Record<QualifierType, QualifierTypeConfig>>
>((configs, config) => ({ ...configs, [config.type]: config }), {});

interface QualifierListItem {
  qualifier: Qualifier;
  config: QualifierTypeConfig;
}

const toArticleQualifier = (
  qualifier: Qualifier,
  order: number,
): ArticleQualifier => ({
  __typename: 'ArticleQualifier',
  order,
  qualifier: {
    __typename: 'Qualifier',
    id: qualifier.id,
    title: qualifier.title,
    type: qualifier.type,
  },
});

const getNextQualifierOrder = (
  articleQualifiers: readonly ArticleQualifier[],
) =>
  articleQualifiers.reduce(
    (maxOrder, articleQualifier) => Math.max(maxOrder, articleQualifier.order),
    -1,
  ) + 1;

const getQualifierGroups = (qualifiers: readonly Qualifier[] | undefined) => {
  const qualifiersByType = (qualifiers ?? [])
    .filter((qualifier) => qualifier.status !== QualifierStatus.Draft)
    .reduce<Partial<Record<QualifierType, Qualifier[]>>>(
      (groups, qualifier) => ({
        ...groups,
        [qualifier.type]: [...(groups[qualifier.type] ?? []), qualifier],
      }),
      {},
    );

  return QUALIFIER_GROUPS.flatMap((group) => {
    const items = group.types.flatMap((type) => {
      const config = qualifierTypeConfigByType[type];
      if (!config) return [];

      return (qualifiersByType[type] ?? []).map((qualifier) => ({
        qualifier,
        config,
      }));
    });

    return items.length ? [{ label: group.label, items }] : [];
  });
};

interface QualifierChipProps {
  qualifier: Qualifier;
  config: QualifierTypeConfig;
  selected: boolean;
  onToggle: () => void;
}

const QualifierChip: FC<QualifierChipProps> = ({
  qualifier,
  config,
  selected,
  onToggle,
}) => {
  const { Icon, main, hover, iconSx } = config;
  return (
    <Chip
      icon={
        selected ? (
          <Icon
            sx={{
              fontSize: '18px !important',
              color: 'white',
              ...iconSx,
            }}
          />
        ) : undefined
      }
      label={qualifier.title}
      onClick={onToggle}
      sx={{
        cursor: 'pointer',
        border: '1px solid',
        borderColor: selected ? main : 'grey.400',
        backgroundColor: selected ? main : 'grey.200',
        color: selected ? 'white' : 'grey.600',
        '& .MuiChip-icon': {
          color: 'white !important',
        },
        '&:hover': {
          backgroundColor: selected ? hover : 'grey.300',
        },
        transition: 'all 0.2s',
      }}
    />
  );
};

interface QualifierGroupProps {
  label: string;
  items: QualifierListItem[];
  isSelected: (id: string) => boolean;
  onToggle: (qualifier: Qualifier) => void;
}

const QualifierGroup: FC<QualifierGroupProps> = ({
  label,
  items,
  isSelected,
  onToggle,
}) => {
  const selectedCount = items.filter(({ qualifier }) =>
    isSelected(qualifier.id),
  ).length;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}
      >
        {label}
        <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
          {selectedCount}/{items.length}
        </Box>
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {items.map(({ qualifier, config }) => (
          <QualifierChip
            key={qualifier.id}
            qualifier={qualifier}
            config={config}
            selected={isSelected(qualifier.id)}
            onToggle={() => onToggle(qualifier)}
          />
        ))}
      </Box>
    </Box>
  );
};

interface QualifiersProps {
  brandKey: BrandKey;
  qualifiers: ArticleQualifier[];
  onChange: (updatedQualifiers: ArticleQualifier[]) => void;
  articleFormat: ArticleFormat;
  label?: string;
}

export const Qualifiers: FC<QualifiersProps> = ({
  brandKey,
  qualifiers,
  onChange,
  articleFormat,
  label,
}) => {
  const classes = useStyles();
  const { data } = useQualifiers({
    first: 100,
    where: { brandKey, format: articleFormat },
    orderBy: [QualifierOrderingInput.title_ASC],
  });

  const qualifierGroups = getQualifierGroups(data?.qualifiers);

  const isChecked = (id: string) =>
    qualifiers.some(({ qualifier }) => qualifier.id === id);

  const handleToggle = (selectedQualifier: Qualifier) => {
    const isSelected = isChecked(selectedQualifier.id);
    const updatedQualifiers = isSelected
      ? qualifiers.filter(
          ({ qualifier }) => qualifier.id !== selectedQualifier.id,
        )
      : [
          ...qualifiers,
          toArticleQualifier(
            selectedQualifier,
            getNextQualifierOrder(qualifiers),
          ),
        ];
    onChange(updatedQualifiers);
  };

  if (!qualifierGroups.length) return null;

  return (
    <FormControl className={classes.formSuggest} variant="standard">
      {label ? (
        <Typography className={classes.subTitle} component="h3">
          {label}
        </Typography>
      ) : (
        <InputLabel shrink>Qualifiers :</InputLabel>
      )}
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '5px',
          padding: 2,
          marginTop: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {qualifierGroups.map((group) => (
          <QualifierGroup
            key={group.label}
            label={group.label}
            items={group.items}
            isSelected={isChecked}
            onToggle={handleToggle}
          />
        ))}
      </Box>
    </FormControl>
  );
};
